import {
  DimeCard,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useBreadcrumbLabel } from '../../../../components/BreadcrumbLabelsContext.js';
import { ListErrorState } from '../../../../components/ListErrorState.js';
import { assertUuidRouteParams } from '../../../../lib/assert-uuid-route-params.js';
import { RouteGuard } from '../../../../lib/route-guard.js';
import { LeafLinkProductListingWizard } from '../../../../modules/leaflink-companies/leaflink-product-listing-wizard.js';
import { LeafLinkWebUrlCompanyContext } from '../../../../modules/leaflink-companies/leaflink-web-url-company-context.js';
import { parseLeafLinkListProductsSearch } from '../../../../modules/leaflink-companies/parse-leaflink-list-products-search.js';
import { toUsableLeafLinkCompanyBrands } from '../../../../modules/leaflink-companies/to-usable-leaflink-company-brands.js';
import { useLeafLinkCompanyQuery } from '../../../../modules/leaflink-companies/use-leaflink-company-query.js';

export const Route = createFileRoute('/integrations/leaflink/$leafLinkIntegrationId/list-products')(
  {
    beforeLoad: ({ params }) => assertUuidRouteParams(params),
    validateSearch: parseLeafLinkListProductsSearch,
    component: () => (
      <RouteGuard route="/integrations/leaflink/$leafLinkIntegrationId/list-products">
        <LeafLinkListProductsPage />
      </RouteGuard>
    ),
  },
);

// eslint-disable-next-line react-refresh/only-export-components
function LeafLinkListProductsPage() {
  const { leafLinkIntegrationId } = Route.useParams();
  const { brandId, search } = Route.useSearch();
  const {
    data: companyData,
    error: companyError,
    refetch: refetchCompany,
  } = useLeafLinkCompanyQuery(leafLinkIntegrationId);
  const company = companyData?.leafLinkCompany ?? null;
  useBreadcrumbLabel(leafLinkIntegrationId, company?.name);

  const filterableBrands = useMemo(
    () => toUsableLeafLinkCompanyBrands(company?.leafLinkCompanyBrand ?? []),
    [company],
  );

  return (
    <LeafLinkWebUrlCompanyContext.Provider value={company}>
      <DimeCard borderless className="gap-0 py-0">
        <DimeCardHeader className="px-0 mb-4">
          <DimeCardTitle className="text-base">
            {company ? `${company.name} — List Products on LeafLink` : 'List Products on LeafLink'}
          </DimeCardTitle>
          <DimeCardDescription className="text-sm">
            List Nudool products on LeafLink, or update products that are already listed.
          </DimeCardDescription>
        </DimeCardHeader>
        {companyError ? (
          <ListErrorState
            title="Could not load this LeafLink company"
            message={companyError.message}
            onRetry={() => void refetchCompany()}
          />
        ) : (
          <LeafLinkProductListingWizard
            leafLinkIntegrationId={leafLinkIntegrationId}
            initialBrandId={brandId}
            initialSearch={search}
            filterableBrands={filterableBrands}
            licenses={company?.leafLinkLicense ?? []}
          />
        )}
      </DimeCard>
    </LeafLinkWebUrlCompanyContext.Provider>
  );
}
